import React from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { cn } from "@/lib/utils";
import {
  X,
  Plus,
  Minus,
  ChevronUp,
  ChevronDown,
  Users,
  Settings,
  Clock,
  Mail,
  User,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  Video,
  UserCheck,
  Save,
  Link as LinkIcon
} from "lucide-react";
import { useTemplates, useTeam } from "@/hooks/api-hooks";
import { toast } from "react-hot-toast";

const questionSchema = z.object({
  id: z.string().optional(),
  text: z.string().min(3, "Question must be at least 3 characters"),
  type: z.enum(["text", "rating", "multiple_choice", "yes_no", "likert"]).optional(),
  questionType: z.enum(["text", "rating", "multiple_choice", "yes_no", "likert"]).optional(),
  options: z.array(z.string()).optional(),
  isRequired: z.boolean().default(true),
  order: z.number().optional(),
  sortOrder: z.number().optional(),
}).transform((data) => ({
  id: data.id || crypto.randomUUID(),
  text: data.text,
  type: data.type || data.questionType || 'text',
  questionType: data.questionType || data.type || 'text',
  options: data.options || [],
  isRequired: data.isRequired !== false,
  order: data.order ?? data.sortOrder ?? 0,
}));

const formSchema = z.object({
  managerId: z.string().min(1, "Please select a manager"),
  employeeId: z.string().min(1, "Please select an employee"),
  workflowType: z.enum(["email", "physical"]).default("email"),
  schedulePattern: z.enum(["bi-weekly", "monthly", "manual", "custom"]).optional(),
  customCron: z.string().optional(),
  startDate: z.string().optional(),
  startTime: z.string().optional(),
  questions: z.array(questionSchema).min(1, "Please add at least one question"),
  templateId: z.string().optional(),
  saveAsTemplate: z.boolean().default(false),
  templateName: z.string().optional(),
  templateDescription: z.string().optional(),
  enablePublicAccess: z.boolean().default(false),
}).refine((data) => {
  if (data.workflowType === "email") {
    if (!data.schedulePattern) return false;
    if (!data.startDate || !data.startTime) return false;
    if (data.schedulePattern === "custom" && (!data.customCron || data.customCron.trim().length === 0)) return false;
  }
  return true;
}, {
  message: "Schedule pattern, start date and time are required for email 1-on-1s",
  path: ["schedulePattern"]
}).refine((data) => {
  if (data.workflowType === "email" && data.schedulePattern === "custom") {
    return !!(data.customCron && data.customCron.trim().length > 0);
  }
  return true;
}, {
  message: "Please provide a cron expression for custom schedule",
  path: ["customCron"]
}).refine((data) => {
  if (data.saveAsTemplate) {
    return !!(data.templateName && data.templateName.trim().length > 0);
  }
  return true;
}, {
  message: "Please provide a template name",
  path: ["templateName"]
}).refine((data) => {
  if (data.managerId && data.employeeId) {
    return data.managerId !== data.employeeId;
  }
  return true;
}, {
  message: "Manager and employee must be different people",
  path: ["employeeId"]
});

export type Setup1on1FormValues = z.infer<typeof formSchema>;

export interface Setup1on1ModalProps {
  isOpen: boolean;
  onClose: () => void;
  teamId?: string;
  employee?: { id: string; firstName: string; lastName: string; fullName?: string; email: string } | null;
  manager?: { id: string; firstName: string; lastName: string; fullName?: string; email: string } | null;
  member?: any;
  currentUser?: any;
  onSubmit: (data: any) => Promise<any> | any;
  existingSetup?: any;
}

const SAMPLE_QUESTIONS = [
  { id: "q1", text: "How are you feeling about your current workload?", type: "rating" as const, options: [], isRequired: true, order: 1 },
  { id: "q2", text: "What's one thing I could do better as your manager?", type: "text" as const, options: [], isRequired: false, order: 2 },
  { id: "q3", text: "Do you feel challenged in your current role?", type: "multiple_choice" as const, options: ["Very challenged", "Somewhat challenged", "Not challenged enough", "Overwhelmed"], isRequired: true, order: 3 },
  { id: "q4", text: "How satisfied are you with your career development opportunities?", type: "rating" as const, options: [], isRequired: false, order: 4 },
];

function QuestionItem({ control, index, onRemove, onMoveUp, onMoveDown, canMoveUp, canMoveDown, watch, setValue }: any) {
  const questionType = watch(`questions.${index}.type`);
  const questionOptions = watch(`questions.${index}.options`) || [];
  return (
    <div className={`relative flex items-start gap-4 p-6 bg-background border rounded-2xl mb-4 transition-smooth border-border hover:border-primary/30`}>
      <div className="flex flex-col gap-2 self-center">
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 rounded-xl" onClick={() => onMoveUp(index)} disabled={!canMoveUp}>
          <ChevronUp className="h-4 w-4" />
        </Button>
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 rounded-xl" onClick={() => onMoveDown(index)} disabled={!canMoveDown}>
          <ChevronDown className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex-1 space-y-4">
        <FormField control={control} name={`questions.${index}.text`} render={({ field }) => (
          <FormItem>
            <FormControl>
              <Input {...field} placeholder="Enter question text" className="h-12 rounded-xl border-2 border-border focus:border-primary focus:ring-0" />
            </FormControl>
          </FormItem>
        )} />

        <div className="flex gap-4 items-center">
          <FormField control={control} name={`questions.${index}.type`} render={({ field }) => (
            <FormItem>
              <FormControl>
                <select {...field} className="px-4 py-3 rounded-xl border-2 border-border bg-background text-sm font-medium focus:border-primary focus:ring-0 !bg-background text-foreground">
                  <option value="text">Text Response</option>
                  <option value="rating">Rating (1-10)</option>
                  <option value="multiple_choice">Multiple Choice</option>
                  <option value="yes_no">Yes/No</option>
                  <option value="likert">Likert Scale</option>
                </select>
              </FormControl>
            </FormItem>
          )} />

          <FormField control={control} name={`questions.${index}.isRequired`} render={({ field }) => (
            <FormItem>
              <FormControl>
                <label className="flex items-center gap-3 bg-muted px-4 py-3 rounded-xl">
                  <Checkbox checked={field.value} onCheckedChange={field.onChange} className="h-4 w-4" />
                  <span className="text-sm font-medium text-brand">Required</span>
                </label>
              </FormControl>
            </FormItem>
          )} />
        </div>

        {questionType === "multiple_choice" && (
          <div className="space-y-3 p-4 bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl border border-primary/20 dark:from-surface-variant dark:to-surface-muted dark:border-border">
            <p className="text-sm font-medium text-primary dark:text-primary mb-3">Multiple Choice Options</p>
            {questionOptions.map((option: any, optionIndex: number) => (
              <div key={optionIndex} className="flex gap-3">
                <Input value={option} onChange={(e) => {
                  const newOptions = [...questionOptions];
                  newOptions[optionIndex] = e.target.value;
                  setValue(`questions.${index}.options`, newOptions);
                }} placeholder={`Option ${optionIndex + 1}`} className="flex-1 h-10 rounded-lg border-2 border-border focus:border-primary focus:ring-0" />
                <Button type="button" variant="ghost" size="icon" className="h-10 w-10 rounded-lg border-2 border-destructive/15 text-destructive hover:bg-destructive/5" onClick={() => {
                  const newOptions = [...questionOptions];
                  newOptions.splice(optionIndex, 1);
                  setValue(`questions.${index}.options`, newOptions);
                }}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => {
              const newOptions = [...questionOptions, ""];
              setValue(`questions.${index}.options`, newOptions);
            }} className="w-full h-10 rounded-lg border-2 border-primary/30 text-primary hover:bg-primary/10">
              <Plus className="h-4 w-4 mr-2" /> Add Option
            </Button>
          </div>
        )}
      </div>

      <Button type="button" variant="ghost" size="icon" className="h-10 w-10 rounded-xl border-2 border-destructive/15 text-destructive hover:bg-destructive/5" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemove(index); }} title="Remove question">
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}

export function Setup1on1Modal({ isOpen, onClose, teamId, employee, manager, onSubmit, existingSetup }: Setup1on1ModalProps) {
  const [activeTab, setActiveTab] = React.useState("setup");

  const { data: teamData } = useTeam(teamId || "");
  const teamMembers = (teamData as any)?.members || (teamData as any)?.data?.members || [];

  const { data: templatesData } = useTemplates({ type: 'one_on_one', includeDefaults: true });
  const templates = templatesData?.data || [];

  const sampleQuestions = React.useMemo(() => SAMPLE_QUESTIONS, []);

  const getDefaultValues = () => {
    const getDefaultStartDate = () => {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow.toISOString().split('T')[0];
    };
    const getDefaultStartTime = () => "09:00";

    if (existingSetup) {
      return {
        managerId: existingSetup.managerId || manager?.id || "",
        employeeId: existingSetup.employeeId || employee?.id || "",
        workflowType: existingSetup.workflowType || "email",
        schedulePattern: existingSetup.schedulePattern || (existingSetup.workflowType === "email" ? "bi-weekly" : "manual"),
        customCron: existingSetup.customSchedule || "",
        startDate: existingSetup.startDate || (existingSetup.workflowType === "email" ? getDefaultStartDate() : ""),
        startTime: existingSetup.startTime || (existingSetup.workflowType === "email" ? getDefaultStartTime() : ""),
        questions: (existingSetup.questions || existingSetup.customQuestions || []).map((q: any) => ({
          id: q.id || crypto.randomUUID(),
          text: q.text,
          type: q.questionType || q.type || 'text',
          questionType: q.questionType || q.type || 'text',
          options: q.options || [],
          isRequired: q.isRequired !== false,
          order: q.order || 0
        })),
        templateId: "",
        saveAsTemplate: false,
        templateName: "",
        templateDescription: "",
        enablePublicAccess: Boolean(existingSetup.enablePublicAccess),
      } as Partial<Setup1on1FormValues> as Setup1on1FormValues;
    }

    return {
      managerId: manager?.id || "",
      employeeId: employee?.id || "",
      workflowType: "email",
      schedulePattern: "bi-weekly",
      customCron: "",
      startDate: getDefaultStartDate(),
      startTime: getDefaultStartTime(),
      questions: [],
      templateId: "",
      saveAsTemplate: false,
      templateName: "",
      templateDescription: "",
      enablePublicAccess: false,
    } as Setup1on1FormValues;
  };

  const form = useForm<Setup1on1FormValues>({ resolver: zodResolver(formSchema), defaultValues: getDefaultValues() });
  const { control, handleSubmit, watch, setValue, getValues, formState: { errors } } = form;
  const { fields: questionFields, append: appendQuestion, remove: removeQuestionByIndex, move: moveQuestion } = useFieldArray({ control, name: "questions" });

  const watchedWorkflowType = watch("workflowType");
  const watchedSchedulePattern = watch("schedulePattern");
  const watchedSaveAsTemplate = watch("saveAsTemplate");
  const watchedManagerId = watch("managerId");

  const safeWatchedQuestions = React.useMemo(() => questionFields.map((field) => ({ ...field, _key: field.id })), [questionFields]);
  const questionCount = safeWatchedQuestions.length;

  React.useEffect(() => {
    if (watchedWorkflowType && !watchedSchedulePattern) {
      const defaultPattern = watchedWorkflowType === 'email' ? 'bi-weekly' : 'manual';
      setValue('schedulePattern', defaultPattern);
    }
  }, [watchedWorkflowType, watchedSchedulePattern, setValue]);

  const loadTemplateQuestions = (templateId: string) => {
    const template = templates.find((t: any) => t.id === templateId);
    if (template && template.questions && Array.isArray(template.questions)) {
      const currentQuestions = getValues("questions") || [];
      const templateQuestions = template.questions.map((q: any) => ({
        ...q,
        id: q.id || `q${Date.now()}-${Math.random()}`,
        type: q.questionType || q.type,
        questionType: q.questionType || q.type,
        options: q.options || [],
        isRequired: q.isRequired !== undefined ? q.isRequired : true,
        order: currentQuestions.length + (q.order || 0),
      }));
      setValue("questions", [...currentQuestions, ...templateQuestions], { shouldValidate: true, shouldDirty: true, shouldTouch: true });
    }
  };

  const addQuestion = React.useCallback(() => {
    appendQuestion({ id: `q${Date.now()}`, text: "", type: "text", questionType: "text", options: [], isRequired: true, order: questionFields.length });
  }, [appendQuestion, questionFields.length]);

  const removeQuestion = React.useCallback((index: number) => {
    if (questionFields.length <= 1) {
      toast("You must have at least one question", { icon: '⚠️' });
      return;
    }
    removeQuestionByIndex(index);
  }, [removeQuestionByIndex, questionFields.length]);

  const moveQuestionUp = (index: number) => { if (index > 0) moveQuestion(index, index - 1); };
  const moveQuestionDown = (index: number) => { if (index < questionFields.length - 1) moveQuestion(index, index + 1); };

  const [isTogglingQuestion, setIsTogglingQuestion] = React.useState<string | null>(null);
  const [isAddingAllQuestions, setIsAddingAllQuestions] = React.useState(false);

  const toggleQuestionSelection = React.useCallback((question: any) => {
    if (isAddingAllQuestions) return;
    if (isTogglingQuestion === question.id) return;
    setIsTogglingQuestion(question.id);

    const currentQuestions = getValues("questions") || [];
    const isCurrentlySelected = currentQuestions.some((q: any) => q.id === question.id);
    let result;
    if (isCurrentlySelected) {
      result = currentQuestions.filter((q: any) => q.id !== question.id);
    } else {
      const newQuestion = { ...question, id: question.id || `q${Date.now()}-${Math.random()}`, type: question.questionType || question.type, questionType: question.questionType || question.type, options: question.options || [], isRequired: question.isRequired !== undefined ? question.isRequired : true, order: currentQuestions.length };
      result = [...currentQuestions, newQuestion];
    }
    setValue("questions", result, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
    setTimeout(() => setIsTogglingQuestion(null), 150);
  }, [setValue, getValues, isTogglingQuestion, isAddingAllQuestions]);

  const toggleAllQuestions = React.useCallback(() => {
    setIsAddingAllQuestions(true);
    const currentQuestions = getValues("questions") || [];
    const currentQuestionIds = new Set(currentQuestions.map((q: any) => q.id));
    const allAdded = SAMPLE_QUESTIONS.every((q: any) => currentQuestionIds.has(q.id));
    let updatedQuestions;
    if (allAdded) {
      const sampleIds = new Set(SAMPLE_QUESTIONS.map((q) => q.id));
      updatedQuestions = currentQuestions.filter((q: any) => !sampleIds.has(q.id));
    } else {
      const newQuestions = SAMPLE_QUESTIONS.filter((q) => !currentQuestionIds.has(q.id)).map((q: any, index: number) => ({ ...q, id: q.id, type: q.questionType || q.type, questionType: q.questionType || q.type, options: q.options || [], isRequired: q.isRequired !== undefined ? q.isRequired : true, order: currentQuestions.length + index }));
      updatedQuestions = [...currentQuestions, ...newQuestions];
    }
    setValue("questions", updatedQuestions, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
    setTimeout(() => setIsAddingAllQuestions(false), 100);
  }, [setValue, getValues]);

  const selectedQuestionIds = React.useMemo(() => new Set(safeWatchedQuestions.map((q) => q.id)), [safeWatchedQuestions]);
  const areAllSampleQuestionsSelected = React.useMemo(
    () => SAMPLE_QUESTIONS.every((question) => selectedQuestionIds.has(question.id)),
    [selectedQuestionIds],
  );

  const getScheduleDescription = (schedulePattern: string | undefined) => {
    switch (schedulePattern) {
      case "bi-weekly": return "Every two weeks";
      case "monthly": return "Once per month";
      case "manual": return "Scheduled manually as needed";
      case "custom": return "Custom cron schedule";
      default: return "";
    }
  };

  const getManagerName = () => {
    if (manager) return `${manager?.firstName} ${manager?.lastName}`;
    if (watchedManagerId && teamMembers.length > 0) {
      const selectedManager = teamMembers.find((member: any) => { const user = member?.user || member; return user.id === watchedManagerId; });
      if (selectedManager) { const user = selectedManager?.user || selectedManager; return `${user.firstName || user.first_name} ${user.lastName || user.last_name}`; }
    }
    return "Not selected";
  };

  const getEmployeeName = () => {
    if (employee) return `${employee?.firstName} ${employee?.lastName}`;
    const watchedEmployeeId = watch("employeeId");
    if (watchedEmployeeId && teamMembers.length > 0) {
      const selectedEmployee = teamMembers.find((member: any) => { const user = member?.user || member; return user.id === watchedEmployeeId; });
      if (selectedEmployee) { const user = selectedEmployee?.user || selectedEmployee; return `${user.firstName || user.first_name} ${user.lastName || user.last_name}`; }
    }
    return "Not selected";
  };

  const handleFormSubmit: (data: Setup1on1FormValues) => Promise<any> = async (data) => {
    const uniqueQuestions = Array.isArray(data.questions)
      ? data.questions.filter(q => q && q.text && q.text.trim().length >= 3).reduce((acc, current) => {
          const exists = acc.find(q => q.text?.trim().toLowerCase() === current.text?.trim().toLowerCase());
          if (!exists) {
            acc.push({ id: current.id || crypto.randomUUID(), text: current.text.trim(), type: current.type || current.questionType || 'text', questionType: current.questionType || current.type || 'text', options: Array.isArray(current.options) ? current.options : [], isRequired: current.isRequired !== false, order: current.order || acc.length });
          }
          return acc;
        }, [] as any[])
      : [];

    const transformedData = {
      ...(existingSetup && { id: existingSetup.id }),
      managerId: data.managerId,
      employeeId: data.employeeId,
      teamId,
      workflowType: data.workflowType,
      ...(data.workflowType === 'email' && { schedulePattern: data.schedulePattern, customCron: data.schedulePattern === 'custom' ? data.customCron : undefined, startDate: data.startDate, startTime: data.startTime }),
      questions: uniqueQuestions,
      saveAsTemplate: data.saveAsTemplate,
      templateName: data.saveAsTemplate ? data.templateName : undefined,
      templateDescription: data.saveAsTemplate ? data.templateDescription : undefined,
      isActive: true,
      enablePublicAccess: data.enablePublicAccess,
      action: existingSetup ? 'update' : 'create',
    };

    try {
      await onSubmit(transformedData);
      toast.dismiss();
      toast.success(existingSetup ? '1-on-1 setup updated successfully' : '1-on-1 setup created successfully');
      onClose();
    } catch (error: any) {
      console.error('1-on-1 setup submission failed:', error);
      toast.dismiss();
      toast.error(error?.message || 'Failed to save 1-on-1 setup');
      throw error;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl w-[95vw] max-h-[95vh] overflow-hidden flex flex-col bg-background !bg-background border border-border">
        <DialogHeader className="pb-6 border-b border-border bg-background">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-primary/20 rounded-2xl flex items-center justify-center">
              <UserCheck className="h-6 w-6 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-2xl font-bold text-brand">{existingSetup ? 'Edit 1-on-1 Setup' : 'Setup 1-on-1'}</DialogTitle>
              <DialogDescription className="text-brand-secondary">Create recurring 1-on-1 sessions configuration</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto bg-background">
          <Form {...form}>
            <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6 p-6 bg-background">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-3 mb-8 h-14 rounded-2xl p-2 bg-muted">
                  <TabsTrigger value="setup" className="flex items-center gap-2 rounded-xl font-semibold"><Settings className="h-4 w-4" />Setup</TabsTrigger>
            <TabsTrigger value="questions" className="flex items-center gap-2 rounded-xl font-semibold"><MessageSquare className="h-4 w-4" />Questions ({questionCount})</TabsTrigger>
                  <TabsTrigger value="review" className="flex items-center gap-2 rounded-xl font-semibold"><CheckCircle className="h-4 w-4" />Review</TabsTrigger>
                </TabsList>

                <TabsContent value="setup" className="space-y-8">
                  {(!manager || !employee) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField control={control} name="managerId" render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-semibold text-brand">Manager</FormLabel>
                          <FormControl>
                            <Select value={field.value} onValueChange={field.onChange}>
                              <SelectTrigger className="h-12 bg-background border-border">
                                <div className="flex items легче gap-2">
                                  <User className="h-4 w-4 text-muted-foreground" />
                                  <SelectValue placeholder="Select a manager" />
                                </div>
                              </SelectTrigger>
                              <SelectContent>
                                {teamMembers?.map((member: any) => {
                                  const user = member?.user || member;
                                  return (
                                    <SelectItem key={user.id} value={user.id}>
                                      <div className="flex items-center gap-2">
                                        <User className="h-4 w-4" />
                                        {user.firstName || user.first_name} {user.lastName || user.last_name}
                                      </div>
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />

                      <FormField control={control} name="employeeId" render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-semibold text-brand">Employee</FormLabel>
                          <FormControl>
                            <Select value={field.value} onValueChange={field.onChange}>
                              <SelectTrigger className="h-12 bg-background border-border">
                                <div className="flex items-center gap-2">
                                  <User className="h-4 w-4 text-muted-foreground" />
                                  <SelectValue placeholder="Select an employee" />
                                </div>
                              </SelectTrigger>
                              <SelectContent>
                                {teamMembers?.map((member: any) => {
                                  const user = member?.user || member;
                                  if (user.id === watch("managerId")) return null;
                                  return (
                                    <SelectItem key={user.id} value={user.id}>
                                      <div className="flex items-center gap-2">
                                        <User className="h-4 w-4" />
                                        {user.firstName || user.first_name} {user.lastName || user.last_name}
                                      </div>
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                    </div>
                  )}

                  <FormField control={control} name="workflowType" render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-semibold text-brand">1-on-1 Type</FormLabel>
                      <FormControl>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          <label className={`cursor-pointer p-6 rounded-2xl border-2 transition-all ${field.value === "email" ? "border-primary bg-primary/10" : "border-border bg-background hover:border-primary/30"}`}>
                            <input type="radio" value="email" checked={field.value === "email"} onChange={() => field.onChange("email")} className="sr-only" />
                            <div className="flex items-start gap-4">
                              <div className={`${field.value === "email" ? "bg-primary/20" : "bg-muted"} w-12 h-12 rounded-2xl flex items-center justify-center`}>
                                <Mail className="h-6 w-6 text-primary" />
                              </div>
                              <div>
                                <h3 className="font-semibold text-brand mb-2">Email 1-on-1</h3>
                                <p className="text-sm text-brand-secondary mb-3">Creates a reusable template with automated scheduling. Employee receives forms via email on schedule.</p>
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className="text-xs">Recurring Template</Badge>
                                  <Badge variant="outline" className="text-xs">Automated</Badge>
                                </div>
                              </div>
                            </div>
                          </label>

                          <label className={`cursor-pointer p-6 rounded-2xl border-2 transition-all ${field.value === "physical" ? "border-primary bg-primary/10" : "border-border bg-background hover:border-primary/30"}`}>
                            <input type="radio" value="physical" checked={field.value === "physical"} onChange={() => field.onChange("physical")} className="sr-only" />
                            <div className="flex items-start gap-4">
                              <div className={`${field.value === "physical" ? "bg-primary/20" : "bg-muted"} w-12 h-12 rounded-2xl flex items-center justify-center`}>
                                <Video className="h-6 w-6 text-primary" />
                              </div>
                              <div>
                                <h3 className="font-semibold text-brand mb-2">Physical 1-on-1</h3>
                                <p className="text-sm text-brand-secondary mb-3">Creates an immediate working form for your meeting. Add context, rate temperature/productivity/support in real-time.</p>
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className="text-xs">Immediate Form</Badge>
                                  <Badge variant="outline" className="text-xs">Live Meeting</Badge>
                                </div>
                              </div>
                            </div>
                          </label>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />

                  {watchedWorkflowType === "email" && (
                    <div className="space-y-6">
                      <div className="border-t border-border pt-6">
                        <h3 className="text-lg font-semibold text-brand mb-4">Schedule Settings</h3>
                        <p className="text-sm text-brand-secondary mb-6">Configure when and how often to send 1-on-1 forms to the employee</p>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <FormField control={control} name="schedulePattern" render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-semibold text-brand">Frequency</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger className="h-12 rounded-xl border-2 border-border focus:border-primary bg-background text-foreground">
                                  <SelectValue placeholder="Select frequency" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent className="bg-background border border-border shadow-clean">
                                <SelectItem value="bi-weekly" className="bg-background hover:bg-muted text-foreground">Bi-weekly</SelectItem>
                                <SelectItem value="monthly" className="bg-background hover:bg-muted text-foreground">Monthly</SelectItem>
                                <SelectItem value="manual" className="bg-background hover:bg-muted text-foreground">Manual</SelectItem>
                                <SelectItem value="custom" className="bg-background hover:bg-muted text-foreground">Custom</SelectItem>
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-brand-secondary"><Clock className="h-3 w-3 inline mr-1" />{getScheduleDescription(watchedSchedulePattern)}</p>
                            <FormMessage />
                          </FormItem>
                        )} />

                        {watchedSchedulePattern === "custom" && (
                          <FormField control={control} name="customCron" render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-semibold text-brand">Custom Cron Expression</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder="0 9 * * 1" className="h-12 rounded-xl border-2 border-border focus:border-primary focus:ring-0" />
                              </FormControl>
                              <p className="text-xs text-brand-secondary">Format: minute hour day month weekday</p>
                              <FormMessage />
                            </FormItem>
                          )} />
                        )}
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <FormField control={control} name="startDate" render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-semibold text-brand">Start Date</FormLabel>
                            <FormControl>
                              <Input {...field} type="date" className="h-12 rounded-xl border-2 border-border focus:border-primary focus:ring-0" min={new Date().toISOString().split('T')[0]} />
                            </FormControl>
                            <p className="text-xs text-brand-secondary">When to send the first 1-on-1 form</p>
                            <FormMessage />
                          </FormItem>
                        )} />

                        <FormField control={control} name="startTime" render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-semibold text-brand">Start Time</FormLabel>
                            <FormControl>
                              <Input {...field} type="time" className="h-12 rounded-xl border-2 border-border focus:border-primary focus:ring-0" />
                            </FormControl>
                            <p className="text-xs text-brand-secondary">Time of day to send forms</p>
                            <FormMessage />
                          </FormItem>
                        )} />
                      </div>
                    </div>
                  )}

                  <FormField control={control} name="enablePublicAccess" render={({ field }) => (
                    <FormItem>
                      <div className="p-6 bg-gradient-to-br from-success/5 to-success/10 rounded-xl border border-success/20 dark:from-surface-variant dark:to-surface-muted dark:border-border">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-success/20 dark:bg-success/10 rounded-full flex items-center justify-center">
                            <LinkIcon className="h-6 w-6 text-success" />
                          </div>
                          <div className="flex-1">
                            <FormControl>
                              <div className="flex items-center gap-3">
                                <Checkbox checked={Boolean(field.value)} onCheckedChange={field.onChange} className="border-success data-[state=checked]:bg-success data-[state=checked]:border-success" />
                                <div>
                                  <FormLabel className="text-lg font-semibold text-foreground cursor-pointer">Enable Public Access</FormLabel>
                                  <p className="text-sm text-foreground-secondary mt-1">Creates a shareable link that anyone can use to access the 1-on-1 {watch("workflowType") === "physical" ? "form" : "setup"} without login.</p>
                                </div>
                              </div>
                            </FormControl>
                            {field.value && (
                              <div className="mt-4 p-4 bg-success/20 rounded-lg">
                                <div className="flex items-center gap-2 text-sm font-medium text-success mb-2">
                                  <LinkIcon className="h-4 w-4" />
                                  Public Link Preview
                                </div>
                                <p className="text-xs text-success">A secure, shareable link will be generated after creation.</p>
                                <div className="flex items-center gap-2 mt-2">
                                  <Badge variant="outline" className="text-xs bg-success/20 text-success border-success/30">No Login Required</Badge>
                                  <Badge variant="outline" className="text-xs bg-success/20 text-success border-success/30">Secure Link</Badge>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )} />

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-primary" />Participants</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="flex items-center gap-4 p-4 bg-muted rounded-xl">
                          <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                            <User className="h-6 w-6 text-primary" />
                          </div>
                          <div>
                            <div className="font-medium text-foreground">Manager</div>
                            <div className="text-sm text-muted-foreground">{getManagerName()}</div>
                          </div>
                        </div>

                        <div className="flex items-center gap-4 p-4 bg-muted rounded-xl">
                          <div className="w-12 h-12 bg-success/20 rounded-full flex items-center justify-center">
                            <User className="h-6 w-6 text-success" />
                          </div>
                          <div>
                            <div className="font-medium text-foreground">Employee</div>
                            <div className="text-sm text-muted-foreground">{getEmployeeName()}</div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="questions" className="space-y-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-xl font-bold text-brand mb-2">Questions</h3>
                      <p className="text-brand-secondary">Build your 1-on-1 question list using templates or custom questions</p>
                    </div>
                    <div className="text-sm text-brand-secondary">0 questions added</div>
                  </div>

                  <div className="p-4 bg-muted rounded-xl">
                    <h4 className="font-semibold text-brand mb-3">Quick Templates</h4>
                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
                      <button type="button" className="p-3 text-left bg-background rounded-lg border border-border hover:border-primary/30 transition-colors">
                        <div className="font-medium text-foreground text-sm">Standard One on_one Template</div>
                        <div className="text-xs text-muted-foreground mt-1">3 questions • one_on_one</div>
                      </button>
                      <button type="button" className="p-3 text-left bg-background rounded-lg border border-border hover:border-primary/30 transition-colors">
                        <div className="font-medium text-foreground text-sm">Weekly One-on-One Template</div>
                        <div className="text-xs text-muted-foreground mt-1">3 questions • one_on_one</div>
                      </button>
                      {templates.length > 0 && templates.map((template: any) => (
                        <button key={template.id} type="button" onClick={() => loadTemplateQuestions(template.id)} className="p-3 text-left bg-background rounded-lg border border-border hover:border-primary/30 transition-colors">
                          <div className="font-medium text-foreground text-sm">{template.name}</div>
                          <div className="text-xs text-muted-foreground mt-1">{template.questions?.length || 0} questions • {template.workflowType || template.templateType}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {errors.questions && (
                    <div className="p-4 bg-destructive/5 border border-destructive/15 rounded-xl">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="h-4 w-4 text-destructive" />
                        <p className="text-sm text-destructive font-medium">{(errors as any).questions.message}</p>
                      </div>
                    </div>
                  )}

                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h4 className="font-semibold text-brand">Pre-defined Questions</h4>
                      <div className="flex items-center gap-3">
                        <Button type="button" variant="outline" size="sm" onClick={toggleAllQuestions} className="flex items-center gap-2 text-xs" disabled={isAddingAllQuestions}>
                          {areAllSampleQuestionsSelected ? <Minus className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
                          {areAllSampleQuestionsSelected ? 'Remove All Questions' : 'Add All Questions'}
                        </Button>
                        <p className="text-sm text-muted-foreground">Click questions to add/remove from your list</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-3 max-h-96 overflow-y-auto">
                      {sampleQuestions.map((question: any) => {
                        const isSelected = selectedQuestionIds.has(question.id);
                        const isLoading = isTogglingQuestion === question.id;
                        return (
                          <div key={question.id} className={cn("flex items-start gap-3 p-4 rounded-xl border-2 transition-all cursor-pointer select-none", isSelected ? "border-primary bg-primary/10 shadow-sm" : "border-border bg-background hover:border-primary/30 hover:bg-primary/5", isLoading && "opacity-60 pointer-events-none")} onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (!isLoading) toggleQuestionSelection(question); }} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (!isLoading) toggleQuestionSelection(question); } }}>
                            <div className="flex items-center justify-center w-5 h-5 mt-0.5">
                              <Checkbox checked={isSelected} disabled className="data-[state=checked]:bg-primary data-[state=checked]:border-primary pointer-events-none" />
                            </div>
                            <div className="flex-1 space-y-2">
                              <p className="text-sm font-medium text-foreground">{question.text}</p>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className={cn("text-xs", (question.type === 'text' || question.questionType === 'text') && "bg-muted text-primary border-border", (question.type === 'rating' || question.questionType === 'rating') && "bg-success/10 text-success border-success/20", (question.type === 'multiple_choice' || question.questionType === 'multiple_choice') && "bg-secondary/10 text-secondary-foreground border-secondary/20", (question.type === 'yes_no' || question.questionType === 'yes_no') && "bg-warning/10 text-warning border-warning/20", (question.type === 'likert' || question.questionType === 'likert') && "bg-accent/10 text-accent-foreground border-accent/20", !question.type && !question.questionType && "bg-muted text-muted-foreground border-muted")}>{(question.type || question.questionType || 'text').replace('_', ' ').toUpperCase()}</Badge>
                                {question.isRequired && (<Badge variant="outline" className="text-xs bg-destructive/5 text-destructive border-destructive/15">Required</Badge>)}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h4 className="font-semibold text-brand">Questions ({questionCount})</h4>
                      <Button type="button" variant="outline" size="sm" onClick={addQuestion} className="flex items-center gap-2"><Plus className="h-4 w-4" />Add Question</Button>
                    </div>

                    <div className="space-y-3">
                      {safeWatchedQuestions.map((question: any, index: number) => (
                        <QuestionItem key={question._key} control={control} index={index} onRemove={removeQuestion} onMoveUp={moveQuestionUp} onMoveDown={moveQuestionDown} canMoveUp={index > 0} canMoveDown={index < safeWatchedQuestions.length - 1} watch={watch} setValue={setValue} />
                      ))}
                    </div>

                    {safeWatchedQuestions.length > 0 && (
                      <div className="pt-3 border-t border-border">
                        <Button type="button" variant="outline" size="sm" onClick={addQuestion} className="flex items-center gap-2 w-full justify-center"><Plus className="h-4 w-4" />Add Question</Button>
                      </div>
                    )}

                    <div className="text-center p-8 border-2 border-dashed border-border rounded-xl bg-muted/50">
                      <MessageSquare className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
                      <p className="text-brand-secondary mb-2">No questions added yet</p>
                      <p className="text-sm text-muted-foreground">Choose from pre-defined questions above or add custom questions</p>
                      <Button type="button" variant="outline" size="sm" onClick={addQuestion} className="flex items-center gap-2 mt-3"><Plus className="h-4 w-4" />Add Your First Question</Button>
                    </div>
                  </div>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2"><Save className="h-5 w-5 text-primary" />Save as Template</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <FormField control={control} name="saveAsTemplate" render={({ field }) => (
                        <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel>Save these questions as a reusable template</FormLabel>
                            <p className="text-xs text-muted-foreground">Templates can be used for future 1-on-1 setups</p>
                          </div>
                        </FormItem>
                      )} />

                      {watchedSaveAsTemplate && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          <FormField control={control} name="templateName" render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-semibold text-brand">Template Name</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder="e.g., Weekly Check-in Questions" className="h-12 rounded-xl border-2 border-border focus:border-primary focus:ring-0" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )} />

                          <FormField control={control} name="templateDescription" render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-semibold text-brand">Description</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder="Optional description" className="h-12 rounded-xl border-2 border-border focus:border-primary focus:ring-0" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )} />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="review" className="space-y-6">
                  <div>
                    <h3 className="text-xl font-bold text-brand mb-6">Review & Confirm</h3>

                    <div className="space-y-6">
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2"><Settings className="h-5 w-5 text-primary" />Setup Summary</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {watch("workflowType") === "email" ? (
                            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
                              <div className="bg-muted p-4 rounded-xl"><div className="text-xs font-medium text-brand-muted mb-1">Type</div><div className="font-semibold text-brand capitalize flex items-center gap-2"><Mail className="h-4 w-4" />Email 1-on-1</div></div>
                              <div className="bg-muted p-4 rounded-xl"><div className="text-xs font-medium text-brand-muted mb-1">Frequency</div><div className="font-semibold text-brand">{getScheduleDescription(watchedSchedulePattern)}</div></div>
                              <div className="bg-muted p-4 rounded-xl"><div className="text-xs font-medium text-brand-muted mb-1">Start Date & Time</div><div className="font-semibold text-brand text-sm">{watch("startDate") && new Date(watch("startDate") as any).toLocaleDateString()}<br />{watch("startTime")}</div></div>
                              <div className="bg-muted p-4 rounded-xl"><div className="text-xs font-medium text-brand-muted mb-1">Questions</div><div className="font-semibold text-brand">{safeWatchedQuestions.length} questions</div></div>
                            </div>
                          ) : (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                              <div className="bg-muted p-4 rounded-xl"><div className="text-xs font-medium text-brand-muted mb-1">Type</div><div className="font-semibold text-brand capitalize flex items-center gap-2"><Video className="h-4 w-4" />Physical 1-on-1</div></div>
                              <div className="bg-muted p-4 rounded-xl"><div className="text-xs font-medium text-brand-muted mb-1">Questions</div><div className="font-semibold text-brand">{safeWatchedQuestions.length} questions</div></div>
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-primary" />Participants</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="bg-gradient-to-r from-primary/5 to-primary/10 p-4 rounded-lg">
                              <div className="flex items-center gap-2 mb-2">
                                <div className="w-8 h-8 bg-surface-variant rounded-full flex items-center justify-center"><User className="h-4 w-4 text-primary" /></div>
                                <div>
                                  <p className="text-xs text-primary font-medium">Manager</p>
                                  <p className="text-sm font-semibold text-gray-800">{getManagerName()}</p>
                                </div>
                              </div>
                            </div>

                            <div className="bg-gradient-to-r from-success/5 to-success/10 p-4 rounded-lg">
                              <div className="flex items-center gap-2 mb-2">
                                <div className="w-8 h-8 bg-success/20 rounded-full flex items-center justify-center"><User className="h-4 w-4 text-success" /></div>
                                <div>
                                  <p className="text-xs text-success font-medium">Employee</p>
                                  <p className="text-sm font-semibold text-gray-800">{(() => { const watchedEmployeeId = watch("employeeId"); if (employee) return `${employee?.firstName} ${employee?.lastName}`; if (watchedEmployeeId && teamMembers.length > 0) { const selectedEmployee = teamMembers.find((member: any) => { const user = member?.user || member; return user.id === watchedEmployeeId; }); if (selectedEmployee) { const user = selectedEmployee?.user || selectedEmployee; return `${user.firstName || user.first_name} ${user.lastName || user.last_name}`; } } return "Not selected"; })()}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2"><MessageSquare className="h-5 w-5 text-primary" />Questions Preview ({safeWatchedQuestions.length})</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            {safeWatchedQuestions.map((question: any, index: number) => (
                              <div key={question.id} className="p-3 rounded-xl bg-muted">
                                <div className="font-medium text-brand">{index + 1}. {question.text}</div>
                                <div className="flex items-center gap-3 mt-2">
                                  <Badge variant="secondary" className="text-xs">
                                    {(question.type === "text" || question.questionType === "text") ? "Text Response" :
                                     (question.type === "rating" || question.questionType === "rating") ? "Rating (1-10)" :
                                     (question.type === "yes_no" || question.questionType === "yes_no") ? "Yes/No" :
                                     (question.type === "likert" || question.questionType === "likert") ? "Likert Scale" :
                                     "Multiple Choice"}
                                  </Badge>
                                  {question.isRequired && (<Badge variant="outline" className="text-xs text-destructive border-destructive/15">Required</Badge>)}
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>

                  <div className="flex justify-between pt-6 border-t border-border bg-background">
                    <Button type="button" variant="outline" onClick={onClose} className="gap-2 rounded-xl">Cancel</Button>
                    <div className="flex gap-3">
                      {activeTab !== "setup" && (
                        <Button type="button" variant="outline" onClick={() => { const tabs = ["setup", "questions", "review"]; const currentIndex = tabs.indexOf(activeTab); if (currentIndex > 0) setActiveTab(tabs[currentIndex - 1]); }} className="gap-2 rounded-xl">Previous</Button>
                      )}
                      {activeTab !== "review" ? (
                        <Button type="button" onClick={() => { const tabs = ["setup", "questions", "review"]; const currentIndex = tabs.indexOf(activeTab); if (currentIndex < tabs.length - 1) setActiveTab(tabs[currentIndex + 1]); }} className="gap-2 bg-gradient-to-r from-primary to-primary-hover hover:from-primary-hover hover:to-primary rounded-xl">Next</Button>
                      ) : (
                        <Button type="submit" className="gap-2 bg-gradient-to-r from-primary to-primary-hover hover:from-primary-hover hover:to-primary rounded-xl"><UserCheck className="h-4 w-4" />{existingSetup ? 'Update 1-on-1 Setup' : 'Create 1-on-1 Setup'}</Button>
                      )}
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
